import { Link } from "@tanstack/react-router";
import { InlineLinks } from "@/components/checklist";

/**
 * The three things anyone arrives here to do, as a numbered index: the headline face,
 * a rule between each, the whole row a link. Was three identical cards, each with an
 * icon in a tinted square, which is the single most generated-looking layout there is.
 * Same three as the header, in the same words.
 */
const PATHS = [
  {
    n: "01",
    title: "I found a tick on me",
    body: "How to take it out, what to keep, and which symptoms are worth a call to a clinician.",
    href: "/tick-bite-tool",
  },
  {
    n: "02",
    title: "What tick is this?",
    body: "Answer what you can see (size, markings, where it was) and narrow eight species down.",
    href: "/tick-identification",
  },
  {
    n: "03",
    title: "How do I avoid them?",
    body: "Repellents that are registered for ticks, treated clothing, the tick check, yards and pets.",
    href: "/prevention",
  },
] as const;

const MORE_LINKS = [
  { to: "/alpha-gal", label: "Alpha-gal syndrome" },
  { to: "/ticks", label: "Tick species" },
  { to: "/diseases", label: "Tick-borne diseases" },
  { to: "/states", label: "Every state" },
  { to: "/resources", label: "Articles" },
] as const;

export function HomePaths() {
  return (
    <section aria-labelledby="home-paths" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      {/* A running head on the section rule, as a printed guide sets one, so the three
          tasks get the full measure instead of sitting beside a mostly empty margin. */}
      <h2 id="home-paths" className="label-caps border-t border-rule pt-3 text-muted-foreground">
        What do you need?
      </h2>
      <ol className="mt-2 lg:mt-6 lg:grid lg:grid-cols-3 lg:border-b lg:border-border">
        {PATHS.map((p) => (
          <li
            key={p.href}
            className="border-b border-border lg:border-b-0 lg:border-l lg:pl-7 lg:first:border-l-0 lg:first:pl-0 lg:[&:not(:last-child)]:pr-7"
          >
            <Link
              to={p.href}
              className="group grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-x-4 py-6 sm:grid-cols-[3.5rem_1fr_auto] sm:py-7 lg:flex lg:h-full lg:flex-col lg:items-start lg:gap-0 lg:pb-8 lg:pt-1"
            >
              <span className="tabular font-display text-lg text-rust-ink">{p.n}</span>
              <span className="lg:mt-3">
                <span className="block font-display text-[1.6rem] font-medium leading-tight text-foreground transition-colors group-hover:text-primary sm:text-[2rem]">
                  {p.title}
                </span>
                <span className="mt-1.5 block max-w-xl text-[0.9375rem] leading-relaxed text-muted-foreground lg:mt-3">
                  {p.body}
                </span>
              </span>
              <span
                aria-hidden
                className="self-center text-xl text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary lg:mt-auto lg:self-start lg:pt-5"
              >
                →
              </span>
            </Link>
          </li>
        ))}
      </ol>
      <nav aria-label="More topics" className="mt-6">
        <InlineLinks className="text-sm">
          {MORE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="link">
                {l.label}
              </Link>
            </li>
          ))}
        </InlineLinks>
      </nav>
    </section>
  );
}
